/* ============================================
   ForwardModal — Select conversation destination
   for forwarding a message. Shows conversation
   list with search + multi-select support.
   ============================================ */
import { useState, useMemo } from 'react'
import { IconSearch, IconSend } from '@/components/icons/Icons'
import type { Conversacion, Contacto } from '@/lib/types'

function channelIcon(canal: string, size: number = 24) {
  const props = { width: size, height: size }
  if (canal === 'whatsapp') return <svg {...props} viewBox="0 0 24 24" fill="#25D366" stroke="none"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
  if (canal === 'email') return <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
  return <span style={{ fontSize: '1.1rem', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>💬</span>
}

interface ForwardModalProps {
  conversations: Conversacion[]
  contactMap: Record<number, Contacto>
  messagePreview: string
  onForward: (conversationIds: number[]) => void
  onClose: () => void
}

export default function ForwardModal({ conversations, contactMap, messagePreview, onForward, onClose }: ForwardModalProps) {
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<number>>(new Set())

  const filtered = useMemo(() => {
    return conversations
      .filter(c => {
        // Hide test/debug
        if (/^(test-|marco-)/.test(c.chat_id || '')) return false
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-/.test(c.chat_id || '')) return false
        return true
      })
      .filter(c => {
        if (!search.trim()) return true
        const q = search.toLowerCase()
        const chatId = c.chat_id?.toLowerCase() || ''
        const contact = c.contacto_id ? contactMap[c.contacto_id] : null
        const name = contact ? `${contact.nombre} ${contact.apellido || ''}`.toLowerCase() : ''
        return chatId.includes(q) || name.includes(q)
      })
  }, [conversations, contactMap, search])

  const toggle = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleForward = () => {
    if (selected.size === 0) return
    onForward(Array.from(selected))
    onClose()
  }

  const getDisplayName = (c: Conversacion) => {
    if (c.contacto_id && contactMap[c.contacto_id]) {
      const ct = contactMap[c.contacto_id]
      return `${ct.nombre} ${ct.apellido || ''}`
    }
    return c.chat_id || 'Sin nombre'
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal forward-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Reenviar mensaje</h2>
          <button className="modal-close" onClick={onClose} type="button">&times;</button>
        </div>

        <div className="forward-modal__body">
          {/* Message preview */}
          <div className="forward-modal__preview">
            <span className="forward-modal__preview-label">Mensaje a reenviar:</span>
            <span className="forward-modal__preview-text">{messagePreview}</span>
          </div>

          {/* Search */}
          <div className="forward-modal__search">
            <IconSearch size={14} />
            <input
              type="text"
              placeholder="Buscar conversacion..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="forward-modal__search-input"
            />
          </div>

          {/* Conversation list */}
          <div className="forward-modal__list">
            {filtered.length === 0 ? (
              <div className="forward-modal__empty">Sin conversaciones</div>
            ) : (
              filtered.map(c => {
                const isChecked = selected.has(c.id)
                return (
                  <button
                    key={c.id}
                    className={`forward-modal__item ${isChecked ? 'forward-modal__item--selected' : ''}`}
                    onClick={() => toggle(c.id)}
                    type="button"
                  >
                    <span className="forward-modal__checkbox">
                      {isChecked && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </span>
                    <span className="forward-modal__item-icon">
                      {channelIcon(c.canal, 20)}
                    </span>
                    <span className="forward-modal__item-name">
                      {getDisplayName(c)}
                    </span>
                  </button>
                )
              })
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} type="button">Cancelar</button>
          <button
            className="btn btn-primary"
            onClick={handleForward}
            disabled={selected.size === 0}
            type="button"
          >
            <IconSend size={14} />
            Reenviar ({selected.size})
          </button>
        </div>
      </div>
    </div>
  )
}
